import { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FieldValues, Path, UnPackAsyncDefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useList } from 'react-use';

export interface FileWithId extends File {
  id?: number;
}

interface Props<T extends FieldValues> {
  id?: string | undefined;
  name: Path<UnPackAsyncDefaultValues<T>>;
  setValue: (name: Path<UnPackAsyncDefaultValues<T>>, next: FileWithId[]) => void;
  // eslint-disable-next-line react/require-default-props
  selectedFiles?: FileWithId[];
}

/**
 * ファイルをドラッグアンドドロップしてアップロードするためコンポーネント
 * 参考：https://react-dropzone.js.org/
 * @param
 * @returns
 */
function FileDrop<T extends FieldValues>({ id, name, setValue, selectedFiles = [] }: Props<T>) {
  // 選択済みのファイル一覧を表示
  const [acceptedFiles, { push: pushAcceptedFiles }] = useList<FileWithId>(selectedFiles);

  // ファイルがドロップされたときは、選択済みファイルリストに追加
  const onDrop = useCallback(
    (files: FileWithId[]) => {
      pushAcceptedFiles(...files);
    },
    [pushAcceptedFiles]
  );

  // ドロップ可能エリアを作るのに使うプロパティを取得
  const { getRootProps, getInputProps, isDragAccept } = useDropzone({ onDrop });

  // 言語情報
  const { t } = useTranslation();

  // 選択済みファイルリストが変更された場合に呼び出される
  useEffect(() => setValue(name, acceptedFiles), [name, acceptedFiles, setValue]);

  return (
    <div id="upFileWrap" className={acceptedFiles.length > 0 ? 'selected' : ''} {...getRootProps()}>
      <label
        id="dropArea"
        className={isDragAccept ? 'dragover' : undefined}
        htmlFor="uploadFile"
        style={{ display: 'flex' }}
      >
        <div className="fileDropLeft" />
        <div className="fileDropCenter">
          <i className="fa-solid fa-arrow-up-from-bracket" />
          <span className="fileDropOr">{t('ファイルをドロップ　または　')}</span>
          <input type="file" id={id} name={name} {...getInputProps()} />
          <div>
            <span className="fileselecttxt">{t('ファイルを選択')}</span>
          </div>
        </div>
        {/* ドロップされたファイル名を表示する */}
        <div className="fileDropRight">
          {acceptedFiles.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div style={{ margin: '2px' }} key={`${name}-${i}`}>
              <span className="selectfile">
                {['xls', 'xlsx', 'xlsm', 'xltx', 'xltm'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                  <i className="fa fa-file-excel" />
                )}
                {['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                  <i className="fa fa-file-word" />
                )}
                {['ppt', 'pptx', 'pptm', 'ppsx', 'ppsm', 'pps', 'potx', 'potm', 'pot'].indexOf(
                  String(x.name.split('.').pop())
                ) !== -1 && <i className="fa fa-file-powerpoint" />}
                {x.name.split('.').pop() === 'csv' && <i className="fa fa-file-csv" />}
                {x.name.split('.').pop() === 'zip' && <i className="fa fa-file-archive" />}
                {x.name.split('.').pop() === 'pdf' && <i className="fa fa-file-pdf" />}
                {x.name.split('.').pop() === 'txt' && <i className="fa fa-file-text" />}
                {['png', 'jpg', 'jpeg', 'eps'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                  <i className="fa fa-file-image" />
                )}
                {[
                  'xls',
                  'xlsx',
                  'xlsm',
                  'xltx',
                  'xltm',
                  'doc',
                  'docx',
                  'docm',
                  'dot',
                  'dotx',
                  'dotm',
                  'ppt',
                  'pptx',
                  'pptm',
                  'ppsx',
                  'ppsm',
                  'pps',
                  'potx',
                  'potm',
                  'pot',
                  'csv',
                  'zip',
                  'pdf',
                  'txt',
                  'png',
                  'jpg',
                  'jpeg',
                  'eps',
                ].indexOf(String(x.name.split('.').pop())) === -1 && <i className="fa fa-file" />}
                {x.name}
              </span>
            </div>
          ))}
          {/*
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-excel" />エクセル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-word" />ワードファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-powerpoint" />パワーポイントファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-csv" />CSVファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-archive" />Zipファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-pdf" />PDFファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-text" />テキストファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file-image" />画像ファイル</span>
            </div>
            <div style={{ margin: '2px' }}>
              <span className="selectfile"><i className="fa fa-file" />その他ファイル</span>
            </div>
          */}
        </div>
      </label>
    </div>
  );
}

FileDrop.defaultProps = {
  id: undefined,
};

export default FileDrop;
